import {parseInt, uniq} from "lodash";
import {EqualCriteria, getRequiredAttributes} from "@esWebAPI/es/model/Criteria";
import {source} from "azure-maps-control";
import {getClusterSetting} from "@esWebAPI/es/model/ClusterSetting";
import {NavUtils} from "@esWebAPI/es/util/NavUtils";
import {LAYER_DATA_LIMITS} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import Digest from "@esWebAPI/es/util/digest";
import {AzureCriteriaAdapter} from "@esWebAPI/es/util/AzureCriteriaAdapter";

import type {Criteria, InCriteria} from "@esWebAPI/es/model/Criteria";
import type {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import type {AggregateExpression, Map, SourceManager} from "azure-maps-control";
import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type Layer from "@esWebAPI/es/map/module/Layer";
import type {ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";


type DatasourceMetaData = {
    layerView: any,
    layerInfo: any,
    isGeoJson: boolean,
    cluster: boolean,
    clusterProperties: any,
    filter: any,
    pbfCriteria: any
}

export class DatasourceManager {

    private readonly _map: Map;
    private readonly territoryModel;
    private readonly sources: SourceManager
    private readonly _layerModule: Layer;

    // Keep track of the properties used to generate the datasource
    private readonly datasourceMetadata: Record<string, DatasourceMetaData>;

    private readonly datasourceReady: Record<string, Promise<source.Source>>;

    private readonly isPreloadPbfProcessing: Record<string, boolean>;
    private readonly pbfPreloadPromise: Record<string, Promise<void>>;

    constructor(map: Map, layerModule: Layer, territoryModel: TerritoryModel) {
        this._map = map;
        this.territoryModel = territoryModel;
        this.sources = map.sources;
        this._layerModule = layerModule;
        this.datasourceMetadata = {};
        this.datasourceReady = {};
        this.isPreloadPbfProcessing = {}
        this.pbfPreloadPromise = {}
    }

    /**
     * Return a Promise that resolve when the datasource is ready
     * @param datasourceId
     */
    public onDataSourceReady(datasourceId: string) {
        return this.datasourceReady[datasourceId];
    }

    private getDatasourceId(layerView: ILayerView, isClustered = true): string {
        let id;
        if (layerView?.$type === "ManageTerritoryBaseLayerView"){
            id = "territory-base-layer-source";
        }
        else if (!isClustered || layerView.geomType !== "POINT") {
            id = `es_${layerView.id}-unclustered`;
        }
        else {
            id = `es_${layerView.id}`;
        }
        return id;
    }

    public getDatasourceUrl(layerView: ILayerView, layerInfo: LayerInfo, isGeoJson: boolean, criteria: Criteria|null, clustered: boolean, pbfCriteria?: Criteria): string {
        const requiredAttributes = this.getRequiredAttributes(layerView);
        let datasourceUrl;

        const criteriaHash = new Digest().update(es.util.criteria.and(criteria, pbfCriteria)).finish();
        if (isGeoJson) {
            const req = {
                format: {
                    $type: 'GeoJsonFormat',
                    requiredAttributes: requiredAttributes,
                    // 4 decimals produce poor shapes for radius buffers
                    decimals: layerView.geomType === "POLYGON" && layerView.$type.startsWith("MapScope") ? 6 : 5
                },
                layers: [{
                    id: layerView.id,
                    criteria: criteria ?? {$type: "All"},
                    version: layerInfo.featureInfo.lastModified
                }],
                version: 42
            }
            datasourceUrl = es.restUrl + es.util.URLON.toUrl(req, `/featureview/geojsonformat?c=${criteriaHash}`)
        } else {

            const resource = "/pbf/tile/";
            datasourceUrl = es.restUrl + resource +
                "{z}/{x}/{y}" +
                "?layerViewId=" + layerView.id +
                "&cluster=" + clustered;

            if (layerInfo?.featureInfo && layerInfo?.featureInfo?.lastModified) {
                datasourceUrl += `&v=${Math.max(layerView.lastModified, layerInfo.featureInfo.lastModified)}`;
            }

            datasourceUrl += `&c=${criteriaHash}`;

            const stylesAttrs = es.map.vector.style.getRequiredAttributes(layerView.styleSettings, this.territoryModel);
            if (stylesAttrs) {
                datasourceUrl += `&styleAttrs=${stylesAttrs}`;
            }

        }

        if (es.isPublicAccess) {
            datasourceUrl += '&TENANT_ID=' + es.tenantId;
        }

        return datasourceUrl;
    }

    public getRequiredAttributes(layerView: ILayerView): string[] {
        const labelAttrs = layerView.styleSettings.labelSettings.labelAttrs;
        const stylerAttrs = es.map.vector.style.getRequiredAttributes(layerView.styleSettings, null);
        const criteriaAttributes = getRequiredAttributes(layerView?.criteria);
        const heatMapAttribute = layerView.styleSettings.heatMapSettings?.attr ? [layerView.styleSettings.heatMapSettings?.attr] : [];
        const selectCriteria = layerView.selectCriteria as EqualCriteria<any>;
        const selectCriteriaAttribute = selectCriteria?.attr ? [selectCriteria.attr] : [];
        const requiredAttributes = [...labelAttrs, ...stylerAttrs, ...criteriaAttributes, ...heatMapAttribute, ...selectCriteriaAttribute];
        return uniq(requiredAttributes);
    }

    private getClusterOptions(layerView: ILayerView, layerInfo: LayerInfo) {

        const isPieClusteringOn = layerView.styleSettings.defaultStyle?.clustering || layerView.styleSettings.defaultStyle?.symbolStyle?.clustering;

        const clusterOpts = isPieClusteringOn ? getClusterSetting("PIE") : getClusterSetting("STANDARD");

        if(layerInfo.featureInfo.count === 0) {
            // This layer doesn't have any features.
            return clusterOpts;
        }
        const layerBounds = layerInfo.featureInfo.bounds;
        const center = es.util.bounds.getCenterPoint(layerBounds);
        const resolution = NavUtils.zoomToResolution(clusterOpts.clusterMaxZoom+1);
        const size = [this._map.getMapContainer().clientWidth, this._map.getMapContainer().clientHeight] as [number, number];

        let mapViewBounds = NavUtils.calculateBoundsFor(resolution, center, size);
        while(es.util.bounds.contains(mapViewBounds, layerBounds) && layerInfo.featureInfo.count > 10000) {
            clusterOpts.clusterMaxZoom += 1;
            mapViewBounds = NavUtils.calculateBoundsFor(NavUtils.zoomToResolution(clusterOpts.clusterMaxZoom), center, size);
        }
        const symbolSize = layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" ?
            layerView.styleSettings.defaultStyle.symbolStyle.size :
            layerView.styleSettings.defaultStyle.size;

        let radiusAdjuster = 0;
        if(isPieClusteringOn){
            radiusAdjuster = parseInt(es.util.localStorage.get("es.clusterCircleRadiusAdjuster") ?? 0);
            clusterOpts.clusterRadius = Math.max(getClusterSetting("PIE").clusterRadius, Math.round(symbolSize)) + radiusAdjuster;
        } else {
            clusterOpts.clusterRadius = Math.max(1, Math.round(symbolSize / 8));
        }

        return clusterOpts;
    }

    private async getPbfServerCriteria(layerView: ILayerView, cluster: boolean): Promise<Criteria|undefined> {
        if (cluster) {
            const styler = await es.map.vector.style.buildStyler(layerView);
            const criteria = es.map.vector.style.getHideFeaturesCriteria(styler);

            if (this.territoryModel) {
                const mapping = this.territoryModel.pointMapping.layerPointMapping.get(layerView.layerMetaId);
                if (this.territoryModel.getFilter() && mapping && mapping?.size > 0) {
                    const visibleFeatIds = Array.from(mapping.values()).filter(v => v.isVisible).map(v => v.featureId);
                    if (visibleFeatIds && visibleFeatIds.length > 0) {
                        const inCriteria: InCriteria<number> = {"$type": "In", attr: "ES_ID", values: visibleFeatIds};
                        /* @ts-ignore */
                        return es.util.criteria.and(criteria, inCriteria) ?? undefined;
                    }
                }
            }
            return criteria;
        }
        return Promise.resolve(undefined);
    }

    /**
     * GeoSpatial filters needs to run on the server for GeoJson datasource.
     */
    private getServerFilterCriteria(layerView: ILayerView): Criteria | null {
        const nearestCriteria = es.util.criteria.find(layerView.criteria, "Nearest");
        const interactsCriteria = es.util.criteria.find(layerView.criteria, "Interacts");
        const withinDistanceCriteria = es.util.criteria.find(layerView.criteria, "WithinDistance");

        if (nearestCriteria || interactsCriteria || withinDistanceCriteria) {
            /* @ts-ignore */
            return es.util.criteria.and(nearestCriteria, interactsCriteria, withinDistanceCriteria);
        }
        return {$type: "All"}
    }

    private getTerritoryFilter(layerView: ILayerView): ExpressionFilterSpecification|undefined {

        if (this.territoryModel.isBaseOrRelatedLayer(layerView.id)) {
            return;
        }

        if (this.territoryModel.isRelatedExtraPointLayer(layerView.id)) {
            const territoryIds = this.territoryModel.getFilter();
            if(!territoryIds || territoryIds.length == 0){
                return undefined;
            }

            const basePointIds = this.territoryModel.getTerritoryFeaturesObjRef().filter(t => !territoryIds.includes(t.ES_ID)).flatMap(t => t.ES_BASE_POINT_IDS) as number[];
            const criteria = {$type: "Not", criteria: {$type: "In", attr: "__id", values: basePointIds}} as const;
            return AzureCriteriaAdapter.convert(criteria, this._map);
        }

        const filteredInTerritoryIds = this.territoryModel.getFilter();
        if (!filteredInTerritoryIds || filteredInTerritoryIds.length == 0){
            return undefined;
        }

        const filteredOutTerritoryIds = this.territoryModel.getTerritoryFeaturesObjRef().filter(t => !filteredInTerritoryIds.includes(t.ES_ID)).map(t => t.ES_ID);
        const territoryPointMapping = this.territoryModel.getPointMapping();
        const pointInFilteredInTerritories: number[] = [];
        for(const territoryId of filteredOutTerritoryIds){
            const pointIds = territoryPointMapping.territoryMapping.get(territoryId)?.get(layerView.layerMetaId)?.keys();
            if(pointIds == undefined){
                continue;
            }
            pointInFilteredInTerritories.push(...pointIds);
        }
        const criteria: Criteria = {
            $type: "And",
            criteria: [{ // Feature as cluster
                $type: "Not",
                criteria: {$type: "In", attr: "ES_ID", values: pointInFilteredInTerritories}
            }, { //Feature id
                $type: "Not",
                criteria: {$type: "In", attr: "__id", values: pointInFilteredInTerritories}
            }]
        };
        return AzureCriteriaAdapter.convert(criteria, this._map);
    }

    private async preloadPbfSupercluster(datasourceId: string, layerView: ILayerView, criteria: Criteria|undefined): Promise<void> {
        if (this.isPreloadPbfProcessing[datasourceId]) {
            return this.pbfPreloadPromise[datasourceId];
        }

        this.isPreloadPbfProcessing[datasourceId] = true

        const getStatus = (taskId: string, resolve: any, reject: any) => {

            es.api.status.getStatus(taskId).then((taskStatus: any) => {
                if (taskStatus.errorMessage?.length > 0) {
                    this.isPreloadPbfProcessing[datasourceId] = false;
                    reject(taskStatus.errorMessage);
                    es.error.handleServerError(taskStatus.errorMessage);
                }
                else if (taskStatus.percentage === 100) {
                    this.isPreloadPbfProcessing[datasourceId] = false;
                    resolve();
                } else {
                    window.setTimeout(function () {
                        getStatus(taskId, resolve, reject);
                    }, 2000);
                }
            });
        }

        const doPreload = async () => {

            const lv = es.util.object.deepCopy(layerView);
            delete lv.styleSettings.colorThematicSettings?.overrideThematic;
            delete lv.styleSettings.sizeThematicSettings?.overrideThematic;

            const asyncTask = await es.api.layerView.pbfPreload(lv, criteria);
            return new Promise<void>((resolve, reject) => {
                getStatus(asyncTask.taskId, resolve, reject);
            });
        };

        this.pbfPreloadPromise[datasourceId] = doPreload();
        return this.pbfPreloadPromise[datasourceId];
    }

    private getClusterProperties(lv: ILayerView) : Record<string, AggregateExpression> {

        const thematicAttrs = [];
        if (lv.styleSettings.colorThematicSettings?.enabled) {
            thematicAttrs.push(lv.styleSettings.colorThematicSettings.attributeName);
        }
        if (lv.styleSettings.symbolThematicSettings?.enabled) {
            thematicAttrs.push(lv.styleSettings.symbolThematicSettings.attributeName);
        }
        if (lv.styleSettings.sizeThematicSettings?.enabled) {
            thematicAttrs.push(lv.styleSettings.sizeThematicSettings.attributeName);
        }

        const props = {
            // For schedule/day trip we need the list of feat in a cluster to check if it contains a stop
            "FEAT_IDS": [
                ['concat', ['accumulated'], ['get','FEAT_IDS'], ','],
                ['concat', ',', ['get', 'ES_ID']]
            ],
            // Use the last feat id as cluster id
            "ES_ID":  [
                ['get', 'ES_ID'],
                ['get', 'ES_ID']
            ]
        } as Record<string, AggregateExpression>;

        thematicAttrs.forEach(attr => {
            props[attr] = [
                ['get', attr],
                ['get', attr]
            ];
        });
        return props;
    }

    private createMataData(layerView: ILayerView, layerInfo: LayerInfo, isGeoJson: boolean, cluster: boolean, clusterProperties?: any, filter?: any, pbfCriteria?: any) {
        return {
            layerView,
            layerInfo,
            isGeoJson,
            cluster,
            clusterProperties,
            filter,
            pbfCriteria
        } as DatasourceMetaData;
    }

    /**
     * Create an empty GeoJSON data source to use with custom layers. (eg. Route symbols)
     * @param layerView
     * @param layerInfo
     * @param datasourceId
     * @param filter Optional filter for the datasource
     */
    public createBlank(layerView: ILayerView, layerInfo: LayerInfo, datasourceId?: string, filter?: ExpressionFilterSpecification){
        if (!datasourceId) {
            datasourceId = this.getDatasourceId(layerView, false);
        }
        const datasource = new source.DataSource(datasourceId, {filter});
        this._map.sources.add(datasource);
        this.datasourceMetadata[datasourceId] = this.createMataData(layerView, layerInfo, true, false, undefined, filter);
        this.datasourceReady[datasourceId] = Promise.resolve(datasource);
        return datasource;
    }

    public isGeoJsonRoute(layerView: ILayerView, layerInfo: LayerInfo): boolean {
        const geomType = layerView.geomType.toUpperCase();
        if (["POLYGON", "LINESTRING"].includes(geomType)) {
            return layerInfo.featureInfo.vertexCount < LAYER_DATA_LIMITS.MAX_VERTEX_FOR_ENTIRE_GEOJSON_LOAD;
        } else if (["POINT", "MULTIPOINT"].includes(geomType)) {
            return layerInfo?.featureInfo?.count <= LAYER_DATA_LIMITS.MAX_FEATS_FOR_ENTIRE_GEOJSON_LOAD;
        } else {
            es.log.warn("isEntireGeoJsonLoaded geom type not explicitly handled. assuming should layerInfo?.featureInfo?.count", geomType, layerView);
            return layerInfo?.featureInfo?.count <= LAYER_DATA_LIMITS.MAX_FEATS_FOR_ENTIRE_GEOJSON_LOAD;
        }
    }

    /**
     * Layer datasource summary for debugging.
     *
     * | Enabled | Loaded  | Meaning |
     * | :---    | :---    | :--- |
     * |  ✅  |  ✅  | enabled and loaded (normal). |
     * |  ❌  |  ❌  | disabled and never loaded (lazy skip working - this row is the memory you're saving). |
     * |  ❌  |  ✅  | disabled after being loaded this session; kept resident so re-enabling is instant. This is the middle-ground policy visible in data. |
     * |  ✅  |  ❌  | should only ever flash briefly while a lazy load is in flight; if you ever see it persist, that's a bug (a layer that thinks it's enabled but never got built) and worth a screenshot. |
     */
    public getDebugInfo(layers: {getLayerView(): ILayerView, getLayerInfo(): LayerInfo}[]) {
        return layers.map(l => {
            const lv = l.getLayerView();
            const li = l.getLayerInfo();
            const dsIds = this.getDatasourceIds([lv.id]);
            const loaded = dsIds.length > 0;
            let sourceType;
            if (loaded) {
                sourceType = [...new Set(dsIds.map(dsId => this.isGeoJson(dsId) ? "geojson" : "pbf"))].join(", ");
            } else {
                // predicted route for layers with no datasource yet (lazy-skipped)
                sourceType = this.isGeoJsonRoute(lv, li) ? "geojson" : "pbf";
            }
            return {
                "Layer": lv.title + " (id: " + lv.id + ")",
                loaded: loaded ? "✅" : "❌",
                enabled: lv.enabled ? "✅" : "❌",
                source: sourceType,
                geomType: lv.geomType,
                featCount: li?.featureInfo?.count,
                vertexCount: li?.featureInfo?.vertexCount
            };
        });
    }

    public async create(layerView: ILayerView, layerInfo: LayerInfo, cluster: boolean, filter?: any, id?: string): Promise<source.Source> {

        const isGeoJson = this.isGeoJsonRoute(layerView, layerInfo);

        const datasourceId = id ?? this.getDatasourceId(layerView, cluster);

        const promise = new Promise<source.Source>((resolve, reject) => {
            (async () => {

                const pbfCriteria =  isGeoJson ? undefined :
                    await this.getPbfServerCriteria(layerView, cluster);

                if (isGeoJson && this?.territoryModel?.hasTerritory()) {
                    const territoryFilter = this.getTerritoryFilter(layerView);
                    if (filter && territoryFilter) {
                        filter = ['all', filter, territoryFilter];
                    } else {
                        filter = filter ?? territoryFilter;
                    }
                }

                const clusterProperties = this.getClusterProperties(layerView);

                this.datasourceMetadata[datasourceId] = this.createMataData(layerView, layerInfo, isGeoJson, cluster, clusterProperties, filter, pbfCriteria)

                const serverCriteria = isGeoJson ? this.getServerFilterCriteria(layerView) : layerView.criteria;
                const datasourceUrl = this.getDatasourceUrl(layerView, layerInfo, isGeoJson, serverCriteria, cluster, pbfCriteria)

                let datasource: source.DataSource | source.VectorTileSource;

                try {
                    if (this.isGeoJson(datasourceId)) {
                        const clusterOptions: any = cluster ? this.getClusterOptions(layerView, layerInfo) : {};
                    const dsOpts = {
                            maxZoom: 22,
                        cluster: cluster,
                        clusterProperties: clusterProperties,
                        ...clusterOptions,
                            filter
                        };
                        datasource = new source.DataSource(datasourceId, dsOpts);
                        await (datasource as source.DataSource).importDataFromUrl(datasourceUrl);
                        this.sources.add(datasource);
                    } else {
                        if (cluster) {
                            await this.preloadPbfSupercluster(datasourceId, layerView, pbfCriteria);
                        }
                        const options = { tiles: [datasourceUrl], maxZoom: 22 };
                        datasource = new source.VectorTileSource(datasourceId, options);
                        this.sources.add(datasource);
                    }
                    resolve(datasource);
                } catch (e) {
                    reject(e);
                    es.log.error("Error Adding Datasource", e);
                }
            })();
        });

        this.datasourceReady[datasourceId] = promise;
        return promise;
    }

    /**
     * Recreate a datasource completely, required when the clustering or the underlying data changes
     * @param datasourceId
     * @param layerView
     * @param layerInfo
     * @param filter
     */
    public async recreate(datasourceId: string, layerView: ILayerView, layerInfo: LayerInfo, filter?: ExpressionFilterSpecification): Promise<source.Source>  {
        const metadata = this.datasourceMetadata[datasourceId];
        if (!metadata) {
            throw new Error(`No DatasourceMetaData found for this datasource id: ${datasourceId}`);
        }

        const cluster = metadata.cluster;

        let promise: Promise<source.Source>;

        if (metadata.isGeoJson) {
            this.remove(datasourceId);
            promise = this.create(layerView, layerInfo, cluster, filter, datasourceId);
        } else {
            promise = new Promise((resolve, _reject) => {
                (async () => {

                    const pbfCriteria = await this.getPbfServerCriteria(layerView, cluster);

                    if (cluster) {
                        await this.preloadPbfSupercluster(datasourceId, layerView, pbfCriteria);
                    }

                    /*
                    // TODO FIXME: @anthony do we really have to save it here?! Bit weird doing it in the datasource manager!
                    // We need to persist the LayerView on the server for to have the updated criteria
                    // but we can't save overrideThematic on the LayerView because it is only for client side styling (TOR9-21725)
                    layerView = es.util.object.deepCopy(layerView);
                    ["colorThematicSettings", "sizeThematicSettings", "symbolThematicSettings"].forEach(s => {
                        if (layerView.styleSettings[s]?.overrideThematic) {
                            delete layerView.styleSettings[s]?.overrideThematic;
                        }
                    });
                    await es.api.layerView.update(layerView);
                    */

                    const serverSideCriteria = this.getServerFilterCriteria(layerView);
                    const datasourceUrl = this.getDatasourceUrl(layerView, layerInfo, false, serverSideCriteria, cluster, pbfCriteria);
                    // https://github.com/mapbox/mapbox-gl-js/issues/2941
                    /* @ts-expect-error */
                    this._map.map.getSource(datasourceId).setTiles([datasourceUrl]);
                    resolve(this._map.sources.getById(datasourceId));
                })()
            });
        }


        this.datasourceReady[datasourceId] = promise;
        return promise;
    }

    /* public async reload(datasourceId: string, layerView: ILayerView, layerInfo: LayerInfo, filter?: ExpressionFilterSpecification) {

        const ds = this.sources.getById(datasourceId);
        if (!ds) {
            const rs = `No datasource found for this id: ${datasourceId}`;
            es.log.warn(rs);
            return Promise.reject(rs);
        }

        if (!this.datasourceMetadata[datasourceId]) {
            const rs = `No datasource metadata found for this id: ${datasourceId}`;
            es.log.warn(rs);
            return Promise.reject(rs);
        }

        if (this.datasourceMetadata[datasourceId].cluster) {
            es.log.warn("Warning: Avoid to call DatasourceManager.reload on a clustered datasource.")
        }

        await this.onDataSourceReady(datasourceId);

        const isGeoJson = this.isGeoJson(datasourceId);
        const cluster = this.datasourceMetadata[datasourceId].cluster;

        const serverCriteria = isGeoJson ? this.getServerFilterCriteria(layerView) : layerView.criteria;
        const pbfCriteria = await this.getPbfServerCriteria(layerView, cluster);

        const datasourceUrl = this.getDatasourceUrl(layerView, layerInfo, isGeoJson, serverCriteria, cluster, pbfCriteria);
    }*/

    public remove(datasourceId: string) {
        try {
            if (this._map.sources.getById(datasourceId)) {
                this._map.sources.remove(datasourceId);
                es.log.log("removed datasource", datasourceId);
            }
        } catch (err) {
            es.log.error("Error removing datasourceId: ", datasourceId, "current layers in the map:", esApp.map.baseMap.map.layers.userLayers);
            throw err;
        }
    }

    public isGeoJson(datasourceId: string) {
        const metadata = this.datasourceMetadata[datasourceId];
        if (!metadata) {
            throw new Error(`No DatasourceMetaData found for this datasource id: ${datasourceId}`);
        }
        return this.datasourceMetadata[datasourceId].isGeoJson;
    }

    public isPbf(datasourceId: string) {
        return !this.isGeoJson(datasourceId);
    }

    public getDatasourceIds(layerViewIds?: number[]): string[] {
        if (!layerViewIds) {
            return Object.keys(this.datasourceMetadata);
        }
        const datasourceIds: string[] = [];
        layerViewIds.forEach(lvId => {
            datasourceIds.push(
                ...Object.entries(this.datasourceMetadata)
                    .filter(e => e[1].layerView.id === lvId)
                    .map(e => e[0])
            );
        });
        return datasourceIds;
    }

    public get layerModule(): Layer {
        return this._layerModule;
    }
}
